// 새 조치 이력 — 매장 · 로봇 칸(2026-10-28 오너).
//   · 매장: 입력한 글자로 등록된 매장을 추천한다("검색창에 입력해둔 단어 토대로"). 고르지 않고 적은 글도 그대로 남는다
//     (조치 이력의 매장은 자유 글이다 — 시트 · 노션에서 온 줄과 같다).
//   · 로봇: 매장을 고르면 그 매장에 지금 있는 로봇을 체크로 고른다. 여러 대가 함께 고장 날 수 있어 여럿 고를 수 있고,
//     시리얼은 "321, 322" 처럼 한 칸에 쉼표로 이어 적는다(시트와 같은 모양 — 서버가 낱개로 나눠 로봇과 잇는다).

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { StoreSummaryDto } from '@bhops/shared';
import { storesApi } from '@/api/stores';
import { robotsApi } from '@/api/robots';
import { useDebouncedValue } from '@/components/pickers/useDebouncedValue';

export interface PickedStore {
  id: string;
  label: string;
}

export function storeLabel(s: Pick<StoreSummaryDto, 'name' | 'branchName'>): string {
  return s.branchName ? `${s.name} ${s.branchName}` : s.name;
}

/** 매장 글 칸 + 추천 목록. 추천을 고르면 `picked` 가 생기고, 글을 고치면 풀린다. */
export function StoreSuggestInput({
  value,
  picked,
  onChange,
  style,
}: {
  value: string;
  picked: PickedStore | null;
  onChange: (text: string, picked: PickedStore | null) => void;
  style: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const term = useDebouncedValue(value.trim(), 200);
  const q = useQuery({
    queryKey: ['stores', 'suggest', term],
    queryFn: () => storesApi.list({ search: term, pageSize: 8 }),
    enabled: open && term.length > 0 && !picked,
    staleTime: 30_000,
  });
  const items = q.data?.items ?? [];
  const show = open && !picked && term.length > 0 && (items.length > 0 || q.isFetched);

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={style}
        maxLength={200}
        value={value}
        onChange={(e) => {
          onChange(e.target.value, null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // 목록을 누르는 사이 칸이 먼저 닫히지 않게 조금 늦게 닫는다.
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        placeholder="매장 이름으로 찾기"
        aria-label="매장"
        aria-autocomplete="list"
        aria-expanded={show}
        data-testid="remediation-create-store"
      />
      {show && (
        <ul role="listbox" aria-label="추천 매장" style={suggestList}>
          {items.length === 0 ? (
            <li style={{ ...suggestItem, color: 'var(--fg-muted)', cursor: 'default' }}>
              맞는 매장이 없습니다. 적은 그대로 남습니다.
            </li>
          ) : (
            items.map((s) => (
              <li
                key={s.id}
                role="option"
                aria-selected={false}
                style={suggestItem}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const label = storeLabel(s);
                  onChange(label, { id: s.id, label });
                  setOpen(false);
                }}
              >
                <span style={{ fontWeight: 600 }}>{storeLabel(s)}</span>
                {s.customer?.displayName && (
                  <span style={{ color: 'var(--fg-muted)', fontSize: 11 }}>
                    {' '}
                    · {s.customer.displayName}
                  </span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/** 고른 매장에 지금 있는 로봇 — 체크로 여럿 고른다. */
export function StoreRobotChecklist({
  storeId,
  selected,
  onChange,
  onLoaded,
}: {
  storeId: string;
  selected: string[];
  onChange: (serials: string[], models: string[]) => void;
  /** 매장 로봇 수 — 0 이면 부르는 쪽이 시리얼을 직접 적는 칸을 보인다. */
  onLoaded?: (count: number) => void;
}) {
  const q = useQuery({
    queryKey: ['robots', 'by-store', storeId],
    queryFn: () => robotsApi.list({ storeId, pageSize: 50 }),
    staleTime: 30_000,
  });
  const count = q.data?.items.length;
  useEffect(() => {
    if (count !== undefined) onLoaded?.(count);
  }, [count, onLoaded]);
  const robots = (q.data?.items ?? []).map((r) => ({
    serial: r.serialShort ?? r.serialLong ?? r.id.slice(0, 6),
    model: r.model.displayName,
  }));
  if (q.isLoading) return <div style={hint}>매장 로봇을 불러오는 중…</div>;
  if (robots.length === 0)
    return (
      <div style={hint}>
        이 매장에 등록된 로봇이 없습니다. 아래에 모델 · 시리얼을 직접 적어 주세요.
      </div>
    );

  function toggle(serial: string) {
    const next = selected.includes(serial)
      ? selected.filter((s) => s !== serial)
      : [...selected, serial];
    const models = [...new Set(robots.filter((r) => next.includes(r.serial)).map((r) => r.model))];
    onChange(next, models);
  }

  return (
    <div role="group" aria-label="매장 로봇" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {robots.map((r) => {
        const on = selected.includes(r.serial);
        return (
          <label key={r.serial} style={{ ...robotChip, ...(on ? robotChipOn : null) }}>
            <input
              type="checkbox"
              checked={on}
              onChange={() => toggle(r.serial)}
              style={{ margin: 0 }}
            />
            <span style={{ fontWeight: 600 }}>{r.serial}</span>
            <span style={{ color: 'var(--fg-muted)' }}>{r.model}</span>
          </label>
        );
      })}
    </div>
  );
}

const suggestList: CSSProperties = {
  position: 'absolute',
  zIndex: 10,
  top: '100%',
  left: 0,
  right: 0,
  margin: '2px 0 0',
  padding: 4,
  listStyle: 'none',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  boxShadow: 'var(--shadow-md)',
  maxHeight: 220,
  overflowY: 'auto',
};
const suggestItem: CSSProperties = {
  padding: '6px 8px',
  fontSize: 13,
  fontWeight: 400,
  borderRadius: 4,
  cursor: 'pointer',
};
const hint: CSSProperties = { fontSize: 12, fontWeight: 400, color: 'var(--fg-muted)' };
const robotChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  border: '1px solid var(--border-card)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  fontWeight: 400,
  cursor: 'pointer',
};
const robotChipOn: CSSProperties = {
  borderColor: 'var(--brand)',
  background: 'color-mix(in srgb, var(--brand) 10%, var(--bg-surface))',
};
